import React, { Component, ReactNode } from 'react';
import { toast } from 'react-toastify';

interface ErrorInfo {
  componentStack: string;
}

interface BaseErrorBoundaryProps {
  children: ReactNode;
  moduleName?: string;
  componentName?: string;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  level: 'global' | 'module' | 'component';
}

interface ErrorState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

interface ErrorContext {
  module?: string;
  component?: string;
  level: string;
  timestamp: string;
  userAgent: string;
  url: string;
}

class BaseErrorBoundary extends Component<BaseErrorBoundaryProps, ErrorState> {
  constructor(props: BaseErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const context: ErrorContext = {
      module: this.props.moduleName,
      component: this.props.componentName,
      level: this.props.level,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    this.setState({ errorInfo });

    // Different handling based on level
    switch (this.props.level) {
      case 'global':
        this.handleGlobalError(error, errorInfo, context);
        break;
      case 'module':
        this.handleModuleError(error, errorInfo, context);
        break;
      case 'component':
        this.handleComponentError(error, errorInfo, context);
        break;
    }

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  handleGlobalError = (error: Error, errorInfo: ErrorInfo, context: ErrorContext) => {
    // Critical error - log to external service
    console.error('🚨 CRITICAL ERROR:', { 
      error: error.message, 
      stack: error.stack,
      errorInfo, 
      context 
    });
    
    // Send to monitoring service (uncomment when ready)
    // errorReporting.captureException(error, { 
    //   level: 'error',
    //   tags: { errorBoundary: 'global' },
    //   extra: { context, errorInfo }
    // });
    
    toast.error('Application error occurred. Please refresh the page.', {
      autoClose: false,
      closeOnClick: false,
    });
  };

  handleModuleError = (error: Error, errorInfo: ErrorInfo, context: ErrorContext) => {
    // Module error - log with module context
    console.error(`❌ ${context.module?.toUpperCase()} MODULE ERROR:`, { 
      error: error.message,
      stack: error.stack,
      errorInfo, 
      context 
    });
    
    // Send to monitoring service (uncomment when ready)
    // errorReporting.captureException(error, { 
    //   level: 'warning',
    //   tags: { errorBoundary: 'module', module: context.module },
    //   extra: { context, errorInfo }
    // });
    
    toast.error(`${context.module} module encountered an error. Other features should work normally.`, {
      autoClose: 8000,
    });
  };

  handleComponentError = (error: Error, errorInfo: ErrorInfo, context: ErrorContext) => {
    // Component error - less critical
    console.warn(`⚠️ Component Error in ${context.component}:`, { 
      error: error.message,
      context,
      errorInfo
    });
    
    // Send to monitoring service (uncomment when ready)
    // errorReporting.captureException(error, { 
    //   level: 'info',
    //   tags: { errorBoundary: 'component', component: context.component },
    //   extra: { context, errorInfo }
    // });
    
    toast.warning(`${context.component} component failed. Trying to recover...`, {
      autoClose: 5000,
    });
  };

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI based on level
      return this.renderDefaultFallback();
    }

    return this.props.children;
  }

  renderDefaultFallback = () => {
    const { level, moduleName, componentName } = this.props;
    const { error } = this.state;

    switch (level) {
      case 'global':
        return (
          <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
            <div className="text-center">
              <div className="mb-4">
                <i className="ri-error-warning-line text-danger" style={{ fontSize: '4rem' }}></i>
              </div>
              <h1 className="mb-3">Something went wrong</h1>
              <p className="text-muted mb-4">
                The application encountered an unexpected error. Please try refreshing the page.
              </p>
              <div className="d-flex gap-2 justify-content-center">
                <button 
                  className="btn btn-primary" 
                  onClick={() => window.location.reload()}
                >
                  <i className="ri-refresh-line me-1"></i>
                  Refresh Page
                </button>
                <button 
                  className="btn btn-outline-secondary" 
                  onClick={this.handleReset}
                >
                  <i className="ri-restart-line me-1"></i>
                  Try Again
                </button>
              </div>
              {process.env.NODE_ENV === 'development' && error && (
                <details className="mt-4 text-start">
                  <summary className="btn btn-link">Show Error Details</summary>
                  <pre className="mt-2 p-3 bg-light border rounded small">
                    <code>{error.stack}</code>
                  </pre>
                </details>
              )}
            </div>
          </div>
        );

      case 'module':
        return (
          <div className="alert alert-danger m-3" role="alert">
            <div className="d-flex align-items-center">
              <i className="ri-error-warning-line fs-4 me-3"></i>
              <div className="flex-grow-1">
                <h4 className="alert-heading mb-2">
                  {moduleName} Module Error
                </h4>
                <p className="mb-3">
                  The {moduleName?.toLowerCase()} section encountered an error. 
                  Other parts of the application should continue to work normally.
                </p>
                <div className="d-flex gap-2">
                  <button 
                    className="btn btn-outline-danger btn-sm" 
                    onClick={this.handleReset}
                  >
                    <i className="ri-refresh-line me-1"></i>
                    Try Again
                  </button>
                  <button 
                    className="btn btn-outline-secondary btn-sm" 
                    onClick={() => window.location.reload()}
                  >
                    <i className="ri-restart-line me-1"></i>
                    Refresh Page
                  </button>
                </div>
              </div>
            </div>
            {process.env.NODE_ENV === 'development' && error && (
              <details className="mt-3">
                <summary className="btn btn-link btn-sm p-0">Show Error Details</summary>
                <pre className="mt-2 p-2 bg-light border rounded small">
                  <code>{error.stack}</code>
                </pre>
              </details>
            )}
          </div>
        );

      case 'component':
        return (
          <div className="alert alert-warning" role="alert">
            <div className="d-flex align-items-center">
              <i className="ri-alert-line me-2"></i>
              <div className="flex-grow-1">
                <small className="mb-1 d-block">
                  <strong>{componentName || 'Component'} Error</strong>
                </small>
                <small className="text-muted">
                  This component failed to load. 
                  <button 
                    className="btn btn-link btn-sm p-0 ms-1" 
                    onClick={this.handleReset}
                  >
                    Try again
                  </button>
                </small>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="alert alert-danger" role="alert">
            <i className="ri-error-warning-line me-2"></i>
            An error occurred. Please try refreshing the page.
          </div>
        );
    }
  };
}

export default BaseErrorBoundary;
